import { Fragment, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import { CalendarIcon } from "lucide-react";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { ApplicationAutocomplete } from "./ApplicationAutocomplete";
import { useApplications } from "@/hooks/useApplications";
import type { VaccineOption } from "@/hooks/useApplications";
import { useTranslation } from "react-i18next";

export type NextApplicationEntry = {
  _key: string;
  item_id: string | null;
  item_label: string;
  interval_days: number | null;
  due_date: Date | null;
  create_appointment?: boolean;
  due_time?: string;
};

type Props = {
  mode: "catalog" | "application";
  entries: NextApplicationEntry[];
  onChange: (entries: NextApplicationEntry[]) => void;
  clinicId?: string;
  petSpeciesId?: string;
  applicationDate?: Date | null;
  readOnly?: boolean;
};

let _keyCounter = 0;
export const makeNextApplicationEntry = (): NextApplicationEntry => ({
  _key: `nak-${++_keyCounter}`,
  item_id: null,
  item_label: "",
  interval_days: null,
  due_date: null,
});

export const buildEntriesFromDefaults = (
  defaults: Array<{ item_id: string; interval_days: number }>,
  applicationDate: Date,
): NextApplicationEntry[] =>
  defaults.map((d) => ({
    _key: `nak-${++_keyCounter}`,
    item_id: d.item_id,
    item_label: "",
    interval_days: d.interval_days,
    due_date: addDays(applicationDate, d.interval_days),
  }));

type EntryRowProps = {
  entry: NextApplicationEntry;
  mode: "catalog" | "application";
  clinicId?: string;
  readOnly: boolean;
  onUpdate: (patch: Partial<NextApplicationEntry>) => void;
  onRemove: () => void;
};

function EntryRow({ entry, mode, clinicId, readOnly, onUpdate, onRemove }: EntryRowProps) {
  const { t } = useTranslation("reports");
  const [searchValue, setSearchValue] = useState(entry.item_label);
  const selectedAutocompleteValue = entry.item_id ? `item:${entry.item_id}` : "";

  const { data: options = [], isLoading } = useApplications({
    searchTerm: searchValue,
    clinicId,
    isActive: true,
    includeItemIds: entry.item_id ? [entry.item_id] : undefined,
  });

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1 min-w-0">
        <ApplicationAutocomplete
          options={options}
          isLoading={isLoading}
          value={searchValue}
          selectedValue={selectedAutocompleteValue}
          disabled={readOnly}
          onChange={(value) => {
            setSearchValue(value);
            const current = options.find((o) => o.value === selectedAutocompleteValue);
            if (!current) return;
            const normalizedValue = value.toLowerCase().trim();
            const normalizedCurrent = (current.label ?? current.rawName ?? "").toLowerCase().trim();
            if (normalizedValue !== normalizedCurrent) {
              onUpdate({ item_id: null, item_label: "" });
            }
          }}
          onSelectOption={(option: VaccineOption, displayName: string) => {
            setSearchValue(displayName);
            onUpdate({ item_id: option.itemId, item_label: displayName });
          }}
        />
      </div>

      {mode === "catalog" ? (
        <div className="w-28 shrink-0">
          <Input
            type="number"
            min={1}
            placeholder={t("vaccines.form.intervalDaysPlaceholder")}
            value={entry.interval_days ?? ""}
            disabled={readOnly}
            onChange={(e) =>
              onUpdate({
                interval_days: e.target.value ? Number(e.target.value) : null,
              })
            }
          />
        </div>
      ) : (
        <div className="w-36 shrink-0">
          <DateFieldPopover
            trigger={
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !entry.due_date && "text-muted-foreground",
                )}
                disabled={readOnly}
              >
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                {entry.due_date
                  ? format(entry.due_date, "dd/MM/yyyy")
                  : t("vaccines.form.selectDate")}
              </Button>
            }
            selected={entry.due_date ?? undefined}
            onSelect={(date) => onUpdate({ due_date: date ?? null })}
          />
        </div>
      )}

      {!readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          aria-label={t("vaccines.form.removeNextApplication")}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export function NextApplicationsList({
  mode,
  entries,
  onChange,
  clinicId,
  readOnly = false,
}: Props) {
  const { t } = useTranslation("reports");

  const updateEntry = (key: string, patch: Partial<NextApplicationEntry>) => {
    onChange(entries.map((e) => (e._key === key ? { ...e, ...patch } : e)));
  };

  const removeEntry = (key: string) => {
    onChange(entries.filter((e) => e._key !== key));
  };

  const addEntry = () => {
    onChange([...entries, makeNextApplicationEntry()]);
  };

  return (
    <div className="space-y-2">
      <Label>{t("vaccines.form.nextApplicationsLabel")}</Label>

      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t("vaccines.form.nextApplicationsEmpty")}
        </p>
      )}

      {entries.map((entry) => (
        <Fragment key={entry._key}>
          <EntryRow
            entry={entry}
            mode={mode}
            clinicId={clinicId}
            readOnly={readOnly}
            onUpdate={(patch) => updateEntry(entry._key, patch)}
            onRemove={() => removeEntry(entry._key)}
          />
        </Fragment>
      ))}

      {!readOnly && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1"
          onClick={addEntry}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("vaccines.form.addNextApplication")}
        </Button>
      )}
    </div>
  );
}
